'use client'
import { ReactNode, memo, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { useDropdown } from './useDropdown'
import { mq, vw, getP18, getTransition, hideScrollbar, rgbAlpha } from '@/styles'

interface IDropdown {
  className?: string,
  trigger: ReactNode | string,
  options: {
    value: string
    path: string
  }[]
}

const getOptions = (options, onClickOption, currentSelection) => {
  return options.map((option, idx) => (
    <li key={idx} className={option.text === currentSelection ? 'active' : ''}>
      <button onClick={() => { onClickOption(option) }}>{option.text}</button>
    </li>
  ))
}

const getInitialSelection = (options) => {
  if(options[0]?.currentVariantId) {
    const selected = options.filter((option) => option.variant.bigcommerce_id === option.currentVariantId)[0]
    return selected?.text ?? options[0]
  } else {
    return options[0]
  }
}

export const Dropdown = memo<IDropdown>(({ className = '', trigger, options }) => {
  const [triggerRef, dropdownRef, expanded, toggleExpanded] = useDropdown()
  const [currentSelection, setSelection] = useState(getInitialSelection(options))

  const onClickOption = useCallback((option) => {
    toggleExpanded()
    setSelection(option.text ?? option )

    // Exception for variants
    option.setCurrentVariant?.(option.variant)
  }, [toggleExpanded, setSelection])

  const Options = useMemo(() => getOptions(options, onClickOption, currentSelection), [options, onClickOption, currentSelection])

  return (
    <DropdownWrapper className={className}>
      <button className='triggerButton' ref={triggerRef} type='button' onClick={toggleExpanded}>
        {trigger}
        <span data-expanded={expanded}>{currentSelection}</span>
      </button>
      <OptionsList ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'}>
        {Options}
      </OptionsList>
    </DropdownWrapper>
  )
})


const DropdownWrapper = styled.div`
  .triggerButton {
    ${getP18()}
    color: ${rgbAlpha('--color-text', .5)};

    span {
      color: var(--color-text);
      position: relative;
      margin-left: ${vw(10, 'mobile')};

      &::after {
        content: url('/images/svg/arrow-down.svg');
        display: inline-block;
        transition: transform 300ms var(--transition-ease);
        margin-left: ${vw(10, 'mobile')};
      }

      &[data-expanded='true']::after {
        transform: rotate(-180deg);
      }
    }
  }

  ${mq.greaterThan('nexus7')} {
    .triggerButton span {
      margin-left: ${vw(10, 'nexus7')};

      &::after { margin-left: ${vw(10, 'nexus7')}; }
    }
  }

  ${mq.greaterThan('tablet')} {
    .triggerButton span {
      margin-left: ${vw(10, 'desktop')};

      &::after { margin-left: ${vw(10, 'desktop')}; }
    }
  }

  ${mq.greaterThan('desktop')} {
    .triggerButton span {
      margin-left: 10px;

      &::after { margin-left: 10px; }
    }
  }
`

const OptionsList = styled(motion.ul)`
  background-color: var(--color-white);
  box-shadow: 0px 2px 4px 0 rgba(0,0,0, .2);
  right: 0;
  top: 100%;
  height: 0;
  opacity: 0;
  overflow-y: auto;
  padding: ${vw(20, 'mobile')} ${vw(30, 'mobile')} ${vw(30, 'mobile')};
  position: absolute;
  width: auto;
  z-index: 10;
  ${hideScrollbar()}

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(20, 'nexus7')} ${vw(30, 'nexus7')} ${vw(30, 'nexus7')};
  }

  ${mq.greaterThan('tablet')} {
    padding: ${vw(20, 'desktop')} ${vw(30, 'desktop')} ${vw(30, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding: 20px 30px 30px;
  }

  li {
    cursor: pointer;
    margin-bottom: ${vw(20, 'mobile')};
    opacity: .4;
    color: var(--color-text);
    width: 100%;
    background-color: var(--color-white);
    transition: opacity 300ms var(--transition-ease), background-color 300ms var(--transition-ease);

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &:active,
    &:hover,
    &.active {
      opacity: 1;
    }

    button {
      ${getP18()}
    }
  }
`

const optionsListVariants:Variants = {
  collapsed: {
    height: '0',
    opacity: 0,
    transitionEnd: {
      display: 'none',
    },
    transition: { ...getTransition() }
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    display: 'block',
    transition: { ...getTransition() }
  }
}